import type { Building } from "building-types/building/buildingTypes";
import type { RemoveFormula } from "building-types/options/formula";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { RoofSystemLevel } from "building-types/settings/roofTypes";
import type { RWTable, Table } from "common/data/table";
import deepequal from "common/utils/deepequal";
import objectFilter from "common/utils/objectKeyFilter";
import objectMap from "common/utils/objectMap";
import shallowCompare from "common/utils/shallowCompare";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { polyLine2Compare } from "geom/polyLine2";
import { generateRoofSystemLevel } from "../elements/multiGableRoofs";
import { evaluateRoofSystems } from "../elements/roofs/evaluateRoofSystems";
import { filterAndGetRoofSystems } from "../elements/roofs/getRoofSystems";
import { addRoofComponentInstances } from "../generationTools/components/componentRoofs";
import memo from "../utils/memo";

function select({
  settings,
  doc: {
    roofSystems,
    roofs,
    optionSets,
    instanceOptionSets,
    edgeModifiers,
    entitySettings,
    settings: buildingDocSettings,
  },
  blocks,
  blockPerimeters,
  rooms,
  roomLevelIndices,
  planarGraphs,
  levels,
  optionChoices,
  mirror,
  settingsSets,
  mirrorTranslationX,
  modifiers,
  modifiersByInstanceChain,
  entityDisabledByGroup,
  roomFloorAssemblies,
  enabledFeatures,
  isolatedEntity,
  idToParents,
  componentToTransforms,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    { roofSettings: settings.roof, roofSystems, roofs },
    definitions,
    optionChoices,
  );

  const roofSettings = evalFormulas(
    settings.roof,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as RemoveFormula<RoofSettings>;

  return {
    blockPerimeters,
    blockLevelIds: objectFilter(
      objectMap(blocks, (x) => x.levelId),
      (x) => !!blockPerimeters[x],
    ),
    blockIsHalfStory: objectMap(blocks, (x) => !!x.isHalfStory),
    rooms,
    roomFloorAssemblies,
    roomLevelIndices,
    polygonPlanarGraphs: planarGraphs,
    roofSystems,
    roofs,
    optionChoices: resolvedOptionChoices,
    levels,
    roofSettings,
    wallSettings: settings.wall,
    mirror,
    mirrorTranslationX,
    modifiers,
    modifiersByInstanceChain,
    edgeModifiers,
    entityDisabledByGroup,
    activeSettingsSets: settingsSets,
    entitySettings,
    settings: buildingDocSettings,
    isolatedEntity,
    idToParents,
    componentToTransforms,
    modelComponents: enabledFeatures.modelComponents,
    wallPlaneEnabled: enabledFeatures.wallPlanes ?? false,
    definitions,
    rawOptionChoices: optionChoices,
  };
}

function compare(a: ReturnType<typeof select>, b: ReturnType<typeof select>) {
  if (!a || !b) {
    return false;
  }

  for (const k in a) {
    if (k === "rooms") {
      // Room comparison is particularly slow and so we do a shallow compare
      if (!shallowCompare(a[k], b[k])) {
        return false;
      }
    } else if (k === "blockPerimeters") {
      const aa = a[k];
      const bb = b[k];
      const oldPropNames = Object.getOwnPropertyNames(aa);
      const newPropNames = Object.getOwnPropertyNames(bb);

      if (oldPropNames.length !== newPropNames.length) {
        return false;
      }

      for (const blockId of oldPropNames) {
        if (!aa[blockId]) {
          return false;
        }

        if (!bb[blockId]) {
          return false;
        }

        if (aa[blockId].length !== bb[blockId].length) {
          return false;
        }

        for (let i = 0; i < aa[blockId].length; i++) {
          if (!polyLine2Compare(aa[blockId][i], bb[blockId][i])) {
            return false;
          }
        }
      }
    } else if (!deepequal(a[k], b[k])) {
      return false;
    }
  }

  return true;
}

function generate(opts: ReturnType<typeof select>): { roofSystemLevels: Table<RoofSystemLevel> } {
  const {
    blockPerimeters,
    blockLevelIds,
    blockIsHalfStory,
    rooms,
    roomFloorAssemblies,
    roomLevelIndices,
    polygonPlanarGraphs,
    levels,
    mirror,
    roofSettings,
    wallSettings,
    mirrorTranslationX,
    roofSystems: initialRoofSystems,
    roofs: initialRoofs,
    modifiers,
    modifiersByInstanceChain,
    edgeModifiers,
    entityDisabledByGroup,
    activeSettingsSets,
    entitySettings,
    settings,
    optionChoices,
  } = opts;

  let { roofSystems: evaluatedRoofSystems, roofs: evaluatedRoofs } = evaluateRoofSystems({
    initialRoofSystems,
    initialRoofs,
    optionChoices,
    entityDisabledByGroup,
  });

  if (opts.modelComponents) {
    const augmented = addRoofComponentInstances(
      evaluatedRoofSystems,
      evaluatedRoofs,
      initialRoofSystems,
      initialRoofs,
      opts.idToParents,
      opts.componentToTransforms,
      levels,
      opts.isolatedEntity,
      {},
      {},
      opts.definitions,
      opts.rawOptionChoices,
    );
    evaluatedRoofSystems = augmented.roofSystems;
    evaluatedRoofs = augmented.roofs;
  }

  const roofSystems = filterAndGetRoofSystems({
    unfilteredRoofSystems: evaluatedRoofSystems,
    unfilteredRoofs: evaluatedRoofs,
    levels,
    blockPerimeters,
    blockLevelIds,
    mirror,
    mirrorTranslationX,
    modifiers,
    edgeModifiers,
    modifiersByInstanceChain,
    idToParents: opts.idToParents,
    isolatedEntity: opts.isolatedEntity,
  });

  const roofSystemLevels: RWTable<RoofSystemLevel> = {};

  // generate perimeter upper floors
  for (const levelId in levels) {
    const level = levels[levelId];

    roofSystemLevels[levelId] = generateRoofSystemLevel({
      levelId: level.id,
      blockLevelIds,
      blockIsHalfStory,
      rooms,
      roomFloorAssemblies,
      roomLevelIndices,
      polygonPlanarGraphs,
      levels,
      blockPerimeters,
      roofSystems,
      roofSettings,
      wallSettings,
      mirror,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings,
      wallPlaneEnabled: opts.wallPlaneEnabled,
    });
  }

  return { roofSystemLevels };
}

export default memo(select, generate, compare);
